import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function PurchaseUpsellModal({ sessionId }) {
  const [offer, setOffer] = useState(null);
  const [open, setOpen] = useState(false);
  const [busyTier, setBusyTier] = useState("");
  const closeRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return undefined;
    let timer;
    api.get(`/upsell/${sessionId}/duration-options`).then(({ data }) => {
      if (!data.eligible || !data.options?.length) return;
      setOffer(data);
      if (!localStorage.getItem(`upsell-dismissed-${sessionId}`)) {
        timer = setTimeout(() => setOpen(true), 900);
      }
    }).catch(() => {});
    return () => clearTimeout(timer);
  }, [sessionId]);

  const close = useCallback(() => {
    localStorage.setItem(`upsell-dismissed-${sessionId}`, "1");
    setOpen(false);
  }, [sessionId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    setTimeout(() => closeRef.current?.focus(), 50);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  const buy = async (option) => {
    setBusyTier(option.target_tier);
    try {
      const { data } = await api.post(`/upsell/${sessionId}/duration`, { target_tier: option.target_tier });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upgrade unavailable");
      setBusyTier("");
      if (e.response?.status === 409) close();
    }
  };

  if (!offer) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[170] flex items-center justify-center bg-black/80 px-4 backdrop-blur-md"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={close} data-testid="purchase-upsell-backdrop">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="duration-upsell-title"
            className="relative w-full max-w-2xl rounded-2xl border border-white/30 bg-black shadow-[0_0_80px_rgba(255,255,255,0.08)]"
            initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }} transition={{ duration: 0.25, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()} data-testid="purchase-upsell-modal">
            <div className="pointer-events-none absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
            <button ref={closeRef} onClick={close} data-testid="upsell-modal-close-btn" aria-label="Close upgrade offer"
              className="absolute right-4 top-4 z-10 rounded-full border border-white/15 p-2 text-white/50 transition-colors hover:border-white/60 hover:text-white">
              <X size={16} />
            </button>

            <div className="relative p-6 sm:p-8">
              <div className="font-mono text-[10px] tracking-[0.24em] text-emerald-200">POST-PURCHASE UPGRADE // INSTANT CHECKOUT</div>
              <h2 id="duration-upsell-title" className="mt-3 font-display text-3xl sm:text-4xl uppercase leading-none">
                Upgrade your duration
              </h2>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-white/55">
                You just bought {offer.source_label} of {offer.product_name}. Upgrade to a longer runtime using the same checkout email — no forms, no discount games, just a faster upgrade.
              </p>

              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                {offer.options.map((option) => (
                  <div key={option.target_tier} className="rounded-2xl border border-white/15 bg-white/[0.03] p-5 transition-colors hover:border-white/45"
                    data-testid={`upgrade-option-${option.target_tier}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-display text-2xl uppercase">{option.label}</div>
                      <span className="rounded-full border border-white/20 px-2 py-1 font-mono text-[9px] tracking-[0.16em] text-white/60">SAME EMAIL</span>
                    </div>
                    <div className="mt-4 flex items-end gap-2">
                      <span className="font-display text-3xl leading-none">${option.price.toFixed(2)}</span>
                    </div>
                    <div className="mt-1 font-mono text-[9px] tracking-[0.14em] text-white/35">{option.quantity > 1 ? `${option.quantity} KEYS · ` : ""}STANDARD DURATION PRICE</div>
                      <button onClick={() => buy(option)} disabled={Boolean(busyTier)}
                        data-testid={`upgrade-cta-${option.target_tier}`}
                        className="btn-inj mt-5 flex w-full items-center justify-center gap-2 px-4 py-3 text-xs disabled:opacity-60">
                        {busyTier === option.target_tier ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpRight size={14} />}
                        UPGRADE TO {option.label}
                      </button>
                    </div>
                ))}
              </div>

              <button onClick={close} data-testid="upsell-modal-decline-btn"
                className="mt-6 w-full rounded-full border border-white/10 px-4 py-3 font-mono text-[10px] tracking-[0.18em] text-white/45 transition-colors hover:border-white/35 hover:text-white">
                DECLINE & VIEW KEYS
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
